import { FormlyFieldConfig } from '@ngx-formly/core';
import { TripettoNode } from '../utils/tripetto-interfaces';
import { convertInterpolationReferences } from '../utils/interpolation-converter';
import { convertMarkdownToHtml } from '../utils/markdown-converter';

/**
 * Generate a readable camelCase key from node name text content.
 * Falls back to nodeId if the name can't produce a usable key.
 */
function generateReadableKey(name: string | undefined, fallbackId: string): string {
	if (!name) return fallbackId;

	// Strip HTML tags to get plain text
	const plainText = name.replace(/<[^>]+>/g, '').trim();
	if (!plainText) return fallbackId;

	// Strip trailing * * (Tripetto required indicators) and other non-alpha chars
	const cleanText = plainText.replace(/\*\s*\*\s*$/, '').trim();
	if (!cleanText) return fallbackId;

	// Convert to camelCase: split on non-alphanumeric, capitalize each word except first
	const words = cleanText
		.replace(/[^a-zA-Z0-9\s]/g, '')
		.trim()
		.split(/\s+/)
		.filter((w) => w.length > 0);

	if (words.length === 0) return fallbackId;

	const key = words
		.map((word, index) =>
			index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
		)
		.join('');

	// Truncate to a reasonable length
	return key.substring(0, 50) || fallbackId;
}

/**
 * Convert Tripetto statement block to Formly static field (display-only)
 */
export function convertStatementBlock(
	node: TripettoNode,
	formlyType: string,
	slotLookup?: Map<string, { alias?: string; nodeName?: string; slotId?: string }>
): FormlyFieldConfig {
	// Convert interpolation references before markdown conversion
	let name = node.name;
	let description = node.description;

	if (slotLookup) {
		name = convertInterpolationReferences(name, slotLookup);
		description = convertInterpolationReferences(description, slotLookup);
	}

	// Generate a readable key from the node name instead of using the hash-based node.id
	const key = generateReadableKey(node.name, node.id || `statement_${Date.now()}_${Math.random()}`);

	return {
		key,
		type: formlyType,
		props: {
			name: convertMarkdownToHtml(name),
			description: convertMarkdownToHtml(description)
		}
	};
}
